import type { ColumnMetadata } from '@/components/maplibre/types';
import { APP_SETTINGS } from '@/lib/config';
import type { Layer } from '@/lib/db-row-types';
import { DASH_ARRAY_PATTERNS } from '@/utils/layer-sources/style-borders';
import { STYLE_MODE } from '@/utils/layer-sources/style-constants';
import {
  type BorderType,
  type ColorMode,
  type LineStyle,
  type NumericMode,
  type PointStyle,
  type PolygonStyle,
  type StyleV2,
  createCategoryColorMode,
  createRangeColorMode,
  createRangeMode,
  createStaticMode,
  isStyleV1,
  isStyleV2,
} from './style-v2';
import { resolveColumnId } from './style-v2';

/**
 * V1 Style format (legacy)
 */
interface StyleConfigV1 {
  circle?: {
    'circle-radius'?: number;
    'circle-color'?: string;
    'circle-opacity'?: number;
    'circle-stroke-color'?: string;
    'circle-stroke-width'?: number;
    'circle-stroke-opacity'?: number;
  };
  fill?: {
    'fill-color'?: string;
    'fill-opacity'?: number;
  };
  line?: {
    'line-color'?: string;
    'line-width'?: number;
    'line-opacity'?: number;
    'line-dasharray'?: number[];
    'line-gap-width'?: number;
  };
  colorLogic?: {
    colorLogicColumn: string;
    colorLogicScheme: string;
    colorLogicType: 'category' | 'value';
    colorLogicEnabled: boolean;
  };
  sizeLogic?: {
    sizeBy: string | null;
    minSize: number;
    maxSize: number;
  };
}

const DEFAULT_VERSION = APP_SETTINGS.CURRENT_STYLE_VERSION;

// Default values from v1
const DEFAULT_V1_VALUES = {
  polygonFillColor: '#2DD4BF',
  polygonFillOpacity: 0.8,
  polygonStrokeColor: '#000000',
  polygonStrokeWidth: 1,
  polygonStrokeOpacity: 1,
  pointColor: '#2DD4BF',
  pointOpacity: 1,
  pointSize: 6,
  pointStrokeColor: '#000000',
  pointStrokeWidth: 1,
  pointStrokeOpacity: 1,
  lineColor: '#2DD4BF',
  lineWidth: 3,
  lineOpacity: 0.8,
  borderType: 'solid' as BorderType,
};

/**
 * Determines border type from v1 line properties
 */
function determineBorderType(lineStyle?: StyleConfigV1['line']): BorderType {
  if (!lineStyle) return DEFAULT_V1_VALUES.borderType;

  const dashArray = lineStyle['line-dasharray'];
  const gapWidth = lineStyle['line-gap-width'];

  if (gapWidth === 2 && (!dashArray || dashArray.length === 0)) {
    return 'double';
  }

  if (!dashArray || dashArray.length === 0) {
    return 'solid';
  }

  const dashStr = JSON.stringify(dashArray);
  const dashedPatternStr = JSON.stringify(DASH_ARRAY_PATTERNS.DASHED);
  const dottedPatternStr = JSON.stringify(DASH_ARRAY_PATTERNS.DOTTED);

  if (dashStr === dashedPatternStr) return 'dashed';
  if (dashStr === dottedPatternStr) return 'dotted';

  return 'solid';
}

/**
 * Converts v1 color logic to v2 ColorMode
 */
function migrateColorLogic(
  v1: StyleConfigV1,
  defaultColor: string,
  columnMetadata?: ColumnMetadata[]
): ColorMode {
  const colorLogic = v1.colorLogic;

  if (!colorLogic || !colorLogic.colorLogicEnabled || !colorLogic.colorLogicColumn) {
    return createStaticMode(defaultColor);
  }

  const columnName = colorLogic.colorLogicColumn;
  const columnId = resolveColumnId(columnName, columnMetadata) as number | null;

  if (colorLogic.colorLogicType === 'category') {
    return createCategoryColorMode(columnId, colorLogic.colorLogicScheme || 'Accent');
  }

  if (colorLogic.colorLogicType === 'value') {
    return createRangeColorMode(columnId, colorLogic.colorLogicScheme || 'green-orange');
  }

  return createStaticMode(defaultColor);
}

/**
 * Converts v1 size logic to v2 NumericMode
 */
function migrateSizeLogic(
  v1: StyleConfigV1,
  defaultSize: number,
  columnMetadata?: ColumnMetadata[]
): NumericMode {
  const sizeLogic = v1.sizeLogic;

  if (!sizeLogic || !sizeLogic.sizeBy) {
    return createStaticMode(defaultSize);
  }

  const columnName = sizeLogic.sizeBy;
  const columnId = resolveColumnId(columnName, columnMetadata) as number | null;

  return createRangeMode(columnId, sizeLogic.minSize || 5, sizeLogic.maxSize || 15);
}

/**
 * Migrates polygon styles from v1 to v2
 */
function migratePolygonStyle(v1: StyleConfigV1, columnMetadata?: ColumnMetadata[]): PolygonStyle {
  const fillColor = v1.fill?.['fill-color'] || DEFAULT_V1_VALUES.polygonFillColor;
  const fillOpacity = v1.fill?.['fill-opacity'] ?? DEFAULT_V1_VALUES.polygonFillOpacity;
  const strokeColor = v1.line?.['line-color'] || DEFAULT_V1_VALUES.polygonStrokeColor;
  const strokeWidth = v1.line?.['line-width'] ?? DEFAULT_V1_VALUES.polygonStrokeWidth;
  const strokeOpacity = v1.line?.['line-opacity'] ?? DEFAULT_V1_VALUES.polygonStrokeOpacity;
  const borderType = determineBorderType(v1.line);

  return {
    fill: {
      color: migrateColorLogic(v1, fillColor, columnMetadata),
      opacity: createStaticMode(fillOpacity),
    },
    border: {
      color: createStaticMode(strokeColor),
      opacity: createStaticMode(strokeOpacity),
      width: createStaticMode(strokeWidth),
      borderType,
    },
  };
}

/**
 * Migrates line styles from v1 to v2
 */
function migrateLineStyle(v1: StyleConfigV1, columnMetadata?: ColumnMetadata[]): LineStyle {
  const lineColor = v1.line?.['line-color'] || DEFAULT_V1_VALUES.lineColor;
  const lineWidth = v1.line?.['line-width'] ?? DEFAULT_V1_VALUES.lineWidth;
  const lineOpacity = v1.line?.['line-opacity'] ?? DEFAULT_V1_VALUES.lineOpacity;
  const borderType = determineBorderType(v1.line);

  return {
    color: migrateColorLogic(v1, lineColor, columnMetadata),
    opacity: createStaticMode(lineOpacity),
    width: createStaticMode(lineWidth),
    borderType,
  };
}

/**
 * Migrates point styles from v1 to v2
 */
function migratePointStyle(v1: StyleConfigV1, columnMetadata?: ColumnMetadata[]): PointStyle {
  const pointColor = v1.circle?.['circle-color'] || DEFAULT_V1_VALUES.pointColor;
  const pointSize = v1.circle?.['circle-radius'] ?? DEFAULT_V1_VALUES.pointSize;
  const pointOpacity = v1.circle?.['circle-opacity'] ?? DEFAULT_V1_VALUES.pointOpacity;
  const strokeColor = v1.circle?.['circle-stroke-color'] || DEFAULT_V1_VALUES.pointStrokeColor;
  const strokeWidth = v1.circle?.['circle-stroke-width'] ?? DEFAULT_V1_VALUES.pointStrokeWidth;
  const strokeOpacity =
    v1.circle?.['circle-stroke-opacity'] ?? DEFAULT_V1_VALUES.pointStrokeOpacity;

  return {
    icon: { mode: STYLE_MODE.FIXED, value: null },
    size: migrateSizeLogic(v1, pointSize, columnMetadata),
    color: migrateColorLogic(v1, pointColor, columnMetadata),
    opacity: createStaticMode(pointOpacity),
    strokeColor: createStaticMode(strokeColor),
    strokeOpacity: createStaticMode(strokeOpacity),
    strokeWidth: createStaticMode(strokeWidth),
  };
}

/**
 * Main migration function: converts v1 style to v2
 *
 * @param v1Style - The v1 style object
 * @param layer - Optional layer object (kept for API compatibility)
 * @param dataSource - Optional data source config (kept for API compatibility)
 * @returns StyleV2 - The migrated v2 style
 */
export function migrateV1ToV2(v1Style: unknown, columnMetadata?: ColumnMetadata[]): StyleV2 {
  if (!isStyleV1(v1Style)) {
    throw new Error('Input is not a v1 style object');
  }

  const v1 = v1Style as StyleConfigV1;

  // Determine which geometry types are present in v1
  const hasPolygon = !!v1.fill;
  const hasLine = !!v1.line && !v1.fill; // Line-only if line exists but fill doesn't
  const hasPoint = !!v1.circle;

  const v2: StyleV2 = {
    version: DEFAULT_VERSION,
  };

  // Migrate based on detected geometry types
  if (hasPolygon) {
    v2.polygon = migratePolygonStyle(v1, columnMetadata);
  }

  if (hasLine || (v1.line && !hasPolygon)) {
    v2.line = migrateLineStyle(v1, columnMetadata);
  }

  if (hasPoint) {
    v2.point = migratePointStyle(v1, columnMetadata);
  }

  // If nothing was detected, create a default polygon style
  if (!v2.polygon && !v2.line && !v2.point) {
    v2.polygon = migratePolygonStyle(v1, columnMetadata);
  }

  return v2;
}

/**
 * Detects the version of a style object
 */
export function detectStyleVersion(style: unknown): 'v1' | 'v2' | 'unknown' {
  if (isStyleV2(style)) return 'v2';
  if (isStyleV1(style)) return 'v1';
  return 'unknown';
}

/**
 * Migration wrapper with error handling and logging
 */
export function migrateStyleWithLogging(
  style: unknown,
  layer?: Layer
): { success: boolean; data?: StyleV2; error?: string } {
  try {
    const version = detectStyleVersion(style);

    if (version === 'v2') {
      console.log('Style is already v2, no migration needed');
      return { success: true, data: style as StyleV2 };
    }

    if (version === 'unknown') {
      console.error('Unknown style format, cannot migrate');
      return { success: false, error: 'Unknown style format' };
    }

    console.log(`Migrating style from v1 to v2 for layer ${layer?.id || 'unknown'}`);
    const v2Style = migrateV1ToV2(style);

    console.log('Migration successful:', {
      layerId: layer?.id,
      hasPolygon: !!v2Style.polygon,
      hasLine: !!v2Style.line,
      hasPoint: !!v2Style.point,
    });

    return { success: true, data: v2Style };
  } catch (error) {
    console.error('Migration failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown migration error',
    };
  }
}
